import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AppModal } from '../components/AppModal';
import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useCreateLineAllowlistEntry,
  useCreateLineChannel,
  useDeleteLineAllowlistEntry,
  useDeleteLineChannel,
  useDiscardLineMessage,
  useLineAllowlist,
  useLineChannels,
  useLineQuarantine,
  useLineStatus,
  useReleaseLineMessage,
} from '../lib/queries/line-intake';

export const Route = createFileRoute('/settings/line-intake')({
  component: LineIntakeSettings,
});

export function LineIntakeSettings() {
  const companyId = useActiveCompanyId();
  const { data: status } = useLineStatus(companyId);
  const { data: channels = [], isPending: channelsPending } = useLineChannels(companyId);
  const deleteChannel = useDeleteLineChannel(companyId);
  const { data: allowlist = [] } = useLineAllowlist(companyId);
  const removeAllow = useDeleteLineAllowlistEntry(companyId);
  const { data: quarantine = [] } = useLineQuarantine(companyId);
  const release = useReleaseLineMessage(companyId);
  const discard = useDiscardLineMessage(companyId);
  const [addChannel, setAddChannel] = useState(false);
  const [addAllow, setAddAllow] = useState(false);

  return (
    <div className="page">
      <PageHead crumbs={['ระบบ', 'รับเอกสารจาก LINE']} title="รับเอกสารจาก LINE (LINE Intake)" />

      <div className="card" style={{ padding: 12, marginBottom: 16 }}>
        <span className={`badge ${status?.enabled ? 'positive' : 'negative'}`}>
          {status?.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
        </span>{' '}
        <span className="muted">จำนวนช่อง: {status?.channelCount ?? 0}</span>{' '}
        <span className="muted">
          เหตุการณ์ล่าสุด:{' '}
          {status?.lastEventAt ? new Date(status.lastEventAt).toLocaleString('th-TH') : '—'}
        </span>
        {status?.lastError && (
          <div className="muted" style={{ color: 'var(--negative)', fontSize: 12 }}>
            ข้อผิดพลาดล่าสุด: {status.lastError}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: '8px 0' }}>ช่อง LINE OA (1 ช่อง = 1 บริษัท)</h3>
        <button className="btn sm primary" onClick={() => setAddChannel(true)}>
          เพิ่มช่อง
        </button>
      </div>
      <div className="tbl-shell" style={{ marginBottom: 24 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>destination</th>
              <th style={{ width: 90 }}>สถานะ</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {channelsPending ? (
              <tr>
                <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : channels.length === 0 ? (
              <tr>
                <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ยังไม่มีช่อง
                </td>
              </tr>
            ) : (
              channels.map((c) => (
                <tr key={c.id}>
                  <td className="code">
                    <b>{c.destination}</b>
                  </td>
                  <td className="muted">{c.active ? 'ใช้งาน' : 'ปิด'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      onClick={() => void deleteChannel.mutateAsync(c.id)}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: '8px 0' }}>รายชื่อผู้ส่งที่ไว้ใจ (allowlist)</h3>
        <button className="btn sm primary" onClick={() => setAddAllow(true)}>
          เพิ่มผู้ส่ง
        </button>
      </div>
      <div className="tbl-shell" style={{ marginBottom: 24 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>lineUserId</th>
              <th>หมายเหตุ</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {allowlist.length === 0 ? (
              <tr>
                <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ยังไม่มีผู้ส่งที่ไว้ใจ
                </td>
              </tr>
            ) : (
              allowlist.map((a) => (
                <tr key={a.id}>
                  <td className="code">{a.lineUserId}</td>
                  <td className="muted">{a.note ?? '—'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      onClick={() => void removeAllow.mutateAsync(a.id)}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '24px 0 8px' }}>กักกัน (quarantine)</h3>
      <div className="tbl-shell">
        <table className="tbl">
          <thead>
            <tr>
              <th>ไฟล์</th>
              <th style={{ width: 160 }}>เหตุผล</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {quarantine.length === 0 ? (
              <tr>
                <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ไม่มีรายการกักกัน
                </td>
              </tr>
            ) : (
              quarantine.map((f) => (
                <tr key={f.id}>
                  <td className="code">{f.fileName ?? '—'}</td>
                  <td className="muted">{f.reason ?? '—'}</td>
                  <td>
                    <button className="btn sm" onClick={() => void release.mutateAsync(f.id)}>
                      ปล่อยกลับเข้าระบบ
                    </button>{' '}
                    <button
                      className="btn sm ghost"
                      style={{ color: 'var(--negative)' }}
                      onClick={() => void discard.mutateAsync(f.id)}
                    >
                      ทิ้ง
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {addChannel && <LineChannelModal onClose={() => setAddChannel(false)} />}
      {addAllow && <LineAllowlistModal onClose={() => setAddAllow(false)} />}
    </div>
  );
}

function LineChannelModal({ onClose }: { onClose: () => void }) {
  const companyId = useActiveCompanyId();
  const create = useCreateLineChannel(companyId);
  const [destination, setDestination] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [channelAccessToken, setChannelAccessToken] = useState('');

  async function save() {
    await create.mutateAsync({ destination, channelSecret, channelAccessToken, active: true });
    onClose();
  }
  return (
    <AppModal
      title="เพิ่มช่อง LINE OA"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!destination || !channelSecret || !channelAccessToken || create.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <label className="muted" style={{ fontSize: 13 }}>
        destination (botUserId ของ LINE OA)
      </label>
      <input
        className="input"
        value={destination}
        onChange={(e) => setDestination(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label className="muted" style={{ fontSize: 13 }}>
        Channel secret
      </label>
      <input
        className="input"
        type="password"
        value={channelSecret}
        onChange={(e) => setChannelSecret(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label className="muted" style={{ fontSize: 13 }}>
        Channel access token
      </label>
      <input
        className="input"
        type="password"
        value={channelAccessToken}
        onChange={(e) => setChannelAccessToken(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
    </AppModal>
  );
}

function LineAllowlistModal({ onClose }: { onClose: () => void }) {
  const companyId = useActiveCompanyId();
  const create = useCreateLineAllowlistEntry(companyId);
  const [lineUserId, setLineUserId] = useState('');
  const [note, setNote] = useState('');

  async function save() {
    await create.mutateAsync({ lineUserId, note: note || undefined });
    onClose();
  }
  return (
    <AppModal
      title="เพิ่มผู้ส่งที่ไว้ใจ"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!lineUserId || create.isPending}
            onClick={() => void save()}
          >
            บันทึก
          </button>
        </>
      }
    >
      <label className="muted" style={{ fontSize: 13 }}>
        lineUserId (หรือ groupId)
      </label>
      <input
        className="input"
        value={lineUserId}
        onChange={(e) => setLineUserId(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label className="muted" style={{ fontSize: 13 }}>
        หมายเหตุ
      </label>
      <input
        className="input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{ width: '100%', marginBottom: 12 }}
      />
    </AppModal>
  );
}
